import { ApiError } from '../../../abstract/api-error';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountApiService } from '../../../core/api-services/account-api.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { AccountDeletionPreview } from '../../../core/response-models/account-deletion-preview.model';
import { deletionBlockerLabel } from '../../../core/utils/account-deletion-utils';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Delete Account Component (web parity for the mobile `DeleteAccountScreen`).
 *
 * Login-gated state machine that mirrors
 * `crop-swipe/lib/features/profile/delete_account_screen.dart`:
 *
 *   loading → preview-error / blocked / reassign / confirm → success
 *
 * The states map to the Hub's `GET /auth/account/deletion-preview` payload:
 *   - **blocked** — `canDelete = false` due to active commitments (work
 *     orders, pending/accepted bids, drone assignments). User must clear
 *     these first; we do NOT offer a delete button.
 *   - **reassign** — `canDelete = false` due to owned orgs with other
 *     members. User must transfer ownership first.
 *   - **confirm** — `canDelete = true`. Shows the compliance disclosure
 *     + the orgs that will be auto-closed + a password re-auth field +
 *     the destructive button.
 *
 * On confirm: `auth.reauthenticate(password)` → `DELETE /auth/account`
 * → renders the one-time recovery code (no back nav from here — deletion
 * is done) → "Done" signs out and routes to `/login`.
 *
 * Recovery is NOT exposed in the UI — see Phase 6 of the account-deletion
 * spec (admin-mediated only; web here just mirrors the mobile coverage).
 *
 * Route: `/profile/delete-account` (under LayoutComponent, AuthGuard).
 * See agent-os/specs/2026-05-29-account-deletion/spec.md.
 */
type ViewState = 'loading' | 'preview-error' | 'blocked' | 'reassign' | 'confirm' | 'success';

@Component({
  selector: 'app-delete-account',
  standalone: true,
  imports: [CommonModule, FormsModule, MaxLengthDirective],
  templateUrl: './delete-account.component.html',
  styleUrls: ['./delete-account.component.scss']
})
export class DeleteAccountComponent implements OnInit {
  private accountApi = inject(AccountApiService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private router = inject(Router);

  viewState: ViewState = 'loading';

  preview: AccountDeletionPreview | null = null;
  previewError: string | null = null;

  password = '';
  deleting = false;
  deleteError: string | null = null;

  /** Populated after a successful DELETE — the one-time recovery code. */
  recoveryCode: string | null = null;

  /** Re-export so the template can call it directly. */
  blockerLabel = deletionBlockerLabel;

  ngOnInit(): void {
    void this.loadPreview();
  }

  /** Initial / retry fetch of the deletion preview. */
  async loadPreview(): Promise<void> {
    this.viewState = 'loading';
    this.previewError = null;
    try {
      const preview = await this.accountApi.deletionPreview();
      this.preview = preview;
      this.viewState = this.classifyPreview(preview);
    } catch (error: unknown) {
      this.previewError = ApiError.fromHttpError(error).errors[0] ?? 'Failed to load deletion preview.';
      this.viewState = 'preview-error';
    }
  }

  /**
   * Map a preview payload to the view to render. Mirrors the branching in
   * the mobile screen — blockers win, then reassign-required, then confirm.
   */
  private classifyPreview(preview: AccountDeletionPreview): ViewState {
    if (preview.blockers.length > 0) {
      return 'blocked';
    }
    if (preview.orgsNeedingReassignment.length > 0) {
      return 'reassign';
    }
    return 'confirm';
  }

  /**
   * Confirm path. Re-authenticates with the typed password (Firebase
   * requires fresh auth before destructive ops), then calls the Hub
   * delete endpoint. On success we land on the recovery-code screen
   * and `_recoveryCode` blocks back-nav.
   */
  async submitDelete(): Promise<void> {
    if (!this.password) {
      this.deleteError = 'Enter your password to confirm.';
      return;
    }
    this.deleting = true;
    this.deleteError = null;

    try {
      // 1. Fresh re-auth. Mirrors mobile `auth.reauthenticate(password)`.
      await this.authService.reauthenticate(this.password);

      // 2. Server-side delete. Hub re-validates preconditions and returns
      //    409 + preview payload if the account became un-deletable since
      //    the page loaded; we reload the preview in that case so the
      //    user sees the new blocker.
      const code = await this.accountApi.deleteAccount();

      this.recoveryCode = code;
      this.viewState = 'success';
    } catch (error: unknown) {
      // ACCOUNT_DELETION_BLOCKED (409) → preconditions changed mid-flight. Reload the
      // preview so the user sees the new blocker rather than a stale "Delete" button.
      if (error instanceof ApiError && error.code === 'ACCOUNT_DELETION_BLOCKED') {
        this.toast.warning(
          'Account state changed — reloading what would be deleted.'
        );
        await this.loadPreview();
      } else {
        // Firebase re-auth errors and hub ApiErrors both carry a displayable message.
        this.deleteError = error instanceof Error && error.message ? error.message : 'Failed to delete account.';
      }
    } finally {
      this.deleting = false;
    }
  }

  /** Copy the recovery code to the clipboard with a toast confirmation. */
  async copyRecoveryCode(): Promise<void> {
    if (!this.recoveryCode) return;
    try {
      await navigator.clipboard.writeText(this.recoveryCode);
      this.toast.success('Recovery code copied');
    } catch {
      // Clipboard may be unavailable (older browsers, insecure context) —
      // the user can still select+copy the SelectableText manually.
      this.toast.warning('Couldn\'t copy automatically — select the code to copy.');
    }
  }

  /**
   * Terminal "Done" — sign out and route to /login. The Firebase user is
   * already gone server-side; this just clears the local session state.
   */
  async signOutAndExit(): Promise<void> {
    try {
      await this.authService.logout();
    } catch {
      // If signOut fails for any reason, still navigate; the auth state
      // listener will reconcile when the user reopens the app.
      await this.router.navigate(['/login']);
    }
  }

  /** "Go back" CTA from blocked / reassign / confirm views. */
  goBack(): void {
    void this.router.navigate(['/profile']);
  }
}
